import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth, hasMinRole } from "@/contexts/AuthContext";
import {
  isFinance, isHR, isProcurement, isProjectManager, isCompliance,
  isAssetManager, isExecutive, can,
} from "@/lib/roles";

/**
 * Route-level access guard. Use to wrap any admin route that should NOT be
 * directly URL-accessible by the wrong role even though the sidebar hides it.
 *
 * Props:
 *   minRole  — min base role required (admin > manager > staff > viewer)
 *   scopes   — array of dept_scope values allowed (managers must match one)
 *   fallback — where to send blocked users (default '/me' for staff, '/' otherwise)
 *
 * In v1.49+, scope-matching specialist officers (finance_officer / hr_officer /
 * procurement_officer) pass the minRole check for their own scope so they can
 * VIEW their module. Backend require_min_role still gates writes.
 */
const SCOPE_CAPABILITY = {
  finance:    isFinance,
  hr:         isHR,
  pm:         isProcurement,         // legacy "pm" was procurement-flavoured
  procurement: isProcurement,
  project:    isProjectManager,
  compliance: isCompliance,
  assets:     isAssetManager,
};

export default function RoleGate({
  children,
  minRole = "staff",
  scopes,
  allowEmployee = false,
  denyRoles,           // ← optional array of role keys explicitly blocked, e.g. ["compliance_officer"]
  perm,                // ← Permission Access Management action key, e.g. "finance.view"
  fallback,
}) {
  const { user } = useAuth();

  if (!user) return null;  // AuthContext gate will already have redirected

  const isAdmin = user.role === "admin" || user.role === "super_admin" || isExecutive(user);
  const isEmployee = user.role === "staff" && !user.dept_scope;
  const target = fallback || (isEmployee ? "/me" : "/");

  // Permission grant/deny wins over role rank
  if (perm) {
    const p = can(user, perm);
    if (p === true) return children;
    if (p === false) return <Navigate to={target} replace />;
  }

  // Explicit deny — runs before everything else so even admins respect it
  // when they're impersonating / acting as the denied role.
  if (Array.isArray(denyRoles) && denyRoles.includes(user.role)) {
    return <Navigate to={target} replace />;
  }

  // Specialist role with matching scope can view its own module even if their
  // numeric rank is below the requested minRole. Backend still gates writes.
  let hasScopeCapability = false;
  if (scopes && scopes.length) {
    hasScopeCapability = scopes.some((s) => {
      const cap = SCOPE_CAPABILITY[s];
      return typeof cap === "function" && cap(user);
    });
  }

  // Min role check (with scope-capability bypass)
  if (!hasMinRole(user, minRole) && !hasScopeCapability) {
    return <Navigate to={target} replace />;
  }

  // Plain employees: explicit allow-list
  if (isEmployee && !allowEmployee) return <Navigate to="/me" replace />;

  // Admins / executives bypass scope check
  if (isAdmin) return children;

  // Scope check for managers / officers
  if (scopes && scopes.length) {
    const scope = user.dept_scope;
    if (hasScopeCapability) return children;       // specialist role wins
    if (scope && !scopes.includes(scope)) return <Navigate to={target} replace />;
  }

  return children;
}
